// ======================================================================
// 📁 File: src/modules/ppe/pages/process/ProcessListPage.jsx
// 📝 Purpose:
//   Process Master – List Page
//   - Gradient header
//   - KPI bar
//   - Search + status filter
//   - Table view
//   - REAL Create/Edit Drawer
// ======================================================================

import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
} from "react";
import axiosClient from "../../../../api/axiosClient";
import { AuthContext } from "../../../../context/AuthContext";

import ProcessKpiBar from "../../components/process/ProcessKpiBar";
import ProcessDrawer from "./ProcessDrawer"; // ✅ FIXED PATH

// ----------------------------------------------------------------------
export default function ProcessListPage() {
  const { permissions } = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedRow, setSelectedRow] = useState(null);

  // ------------------------------------------------------------
  // Load Processes
  // ------------------------------------------------------------
  const loadProcesses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(
        "/api/ppe/masters/processes"
      );
      setRows(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load processes", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProcesses();
  }, [loadProcesses]);

  // ------------------------------------------------------------
  // Drawer handlers
  // ------------------------------------------------------------
  const openCreateDrawer = () => {
    setSelectedRow(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const openEditDrawer = (row) => {
    setSelectedRow(row);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRow(null);
  };

  // ------------------------------------------------------------
  // Filtering
  // ------------------------------------------------------------
  const filteredRows = rows.filter((r) => {
    const q = search.toLowerCase();

    const matchesSearch =
      !q ||
      String(r.process_code).toLowerCase().includes(q) ||
      String(r.process_name).toLowerCase().includes(q);

    const matchesStatus =
      status === "ALL" ||
      (status === "ACTIVE" && r.is_active === true) ||
      (status === "INACTIVE" && r.is_active === false);

    return matchesSearch && matchesStatus;
  });

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div>
      {/* ====================================================== */}
      {/* Header */}
      {/* ====================================================== */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #0f766e 0%, #14b8a6 50%, #5eead4 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>Process Master</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Manufacturing process definitions
          </div>
        </div>

        {permissions?.PROCESS_CREATE?.create && (
          <button
            className="btn btn-light"
            onClick={openCreateDrawer}
          >
            + Create Process
          </button>
        )}
      </div>

      {/* ====================================================== */}
      {/* KPI BAR */}
      {/* ====================================================== */}
      <ProcessKpiBar rows={rows} />

      {/* ====================================================== */}
      {/* Filters */}
      {/* ====================================================== */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <input
          className="form-control"
          placeholder="Search by code or name"
          style={{ maxWidth: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="ALL">All</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>

        <button
          className="btn btn-primary"
          onClick={loadProcesses}
          disabled={loading}
        >
          Apply
        </button>
      </div>

      {/* ====================================================== */}
      {/* Table */}
      {/* ====================================================== */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Setup (min)</th>
                <th>Run (min)</th>
                <th>Move (min)</th>
                <th>Scrap %</th>
                <th>Status</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="text-center text-muted"
                  >
                    No processes found
                  </td>
                </tr>
              )}

              {filteredRows.map((r) => (
                <tr key={r.id}>
                  <td>{r.process_code}</td>
                  <td>{r.process_name}</td>
                  <td>{r.setup_time_minutes}</td>
                  <td>{r.run_time_minutes}</td>
                  <td>{r.move_time_minutes}</td>
                  <td>{r.standard_scrap_percent}</td>
                  <td>
                    {r.is_active ? "Active" : "Inactive"}
                  </td>
                  <td>
                    {permissions?.PROCESS_EDIT?.edit && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openEditDrawer(r)}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ====================================================== */}
      {/* REAL DRAWER */}
      {/* ====================================================== */}
      <ProcessDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={selectedRow}
        onClose={closeDrawer}
        onSaved={loadProcesses}
      />
    </div>
  );
}
